import {
  Root,
  Trigger,
  Portal,
  Content,
  ScrollUpButton,
  Viewport,
  Value,
  ScrollDownButton,
} from '@radix-ui/react-select';
import type { JSX, ReactNode } from 'react';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { Icon } from '../Icon';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { inputCss } from '../../css/inputCss';

const StyledTrigger = styled(Trigger)<{ $colorScheme: ColorSchemeTypes; $fullWidth?: boolean }>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  ${inputCss};
  padding: var(--wui-input-vertical-padding) var(--wui-input-horizontal-padding);
  background-color: var(--wui-input-color-bg);
  border: none;
  outline: 1px solid var(--wui-input-color-border);
  outline-offset: -2px;
  border-radius: var(--wui-input-border-radius);
  min-width: 200px;
  text-align: left;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  width: ${({ $fullWidth = false }) => ($fullWidth ? '100%' : 'auto')};
  font-size: var(--wui-input-font-size);
  line-height: var(--wui-input-line-height);
  color: var(--wui-input-color-text);
  gap: var(--wui-space-01);

  > span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &[aria-expanded='true'],
  &:focus {
    outline: 2px solid var(--wui-color-focus-ring);
  }

  &[aria-invalid='true'] {
    outline: 2px solid var(--wui-input-color-border-error);
  }

  &:disabled {
    outline-color: var(--wui-color-border-disabled);
    background-color: var(--wui-color-bg-surface-disabled);
    cursor: not-allowed;
  }

  &[data-placeholder] {
    color: var(--wui-input-placeholder);
  }
`;

const StyledContent = styled(Content)`
  --wui-select-content-border: var(--wui-color-border);
  --wui-select-content-bg: var(--wui-color-bg-surface);
  --wui-select-content-border-radius: var(--wui-border-radius-02);
  --wui-select-option-padding: var(--wui-space-02);
  --wui-select-option-inner-gap: var(--wui-space-03);
  --wui-select-option-bg-hover: var(--wui-color-bg-surface-hover);

  overflow: hidden;
  background-color: var(--wui-select-content-bg);
  border: 1px solid var(--wui-select-content-border);
  border-radius: var(--wui-select-content-border-radius);
  box-shadow: var(--wui-elevation-01);
  padding: var(--wui-select-option-padding);
  min-width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
  z-index: var(--wui-zindex-select);

  [role='separator'] {
    margin: var(--wui-space-02) 0;
  }
`;

const scrollButtonStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const StyledScrollDownButton = styled(ScrollDownButton)`
  ${scrollButtonStyles}
`;

const StyledScrollUpButton = styled(ScrollUpButton)`
  ${scrollButtonStyles}
`;

export type SelectProps = {
  /**
   * Sets the validity status of the input
   *
   * @default false
   */
  'aria-invalid'?: boolean;

  /**
   * A group of `Option` or `OptionGroup`s
   */
  children: ReactNode;

  /**
   * The color scheme of the select.
   *
   * @default inherit
   */
  colorScheme?: ColorSchemeTypes;

  /**
   * When true, the select can't be interacted with.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * @ignore
   * Force the Select to be open without user interaction (for debugging and CI)
   */
  forceOpen?: boolean;
  /**
   * If true, the select will fill its container
   */
  fullWidth?: ResponsiveObject<boolean> | boolean;

  /**
   * Called when the select is opened or closed
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Called when a value is changed.
   */
  onChange?: (value: string) => void;

  /**
   * Text shown when no value is selected
   */
  placeholder?: string;

  /**
   * The controlled value. Must be used in conjunction with `onChange`.
   */
  value?: string;
};

/**
 * Display a list of options and choose one of them. Replacement for the native Select HTML element.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      colorScheme = 'inherit',
      children,
      onOpenChange = () => null,
      onChange = () => null,
      placeholder = 'Select...',
      fullWidth = false,
      forceOpen = false,
      ...props
    },
    forwardedRef,
  ): JSX.Element => {
    const responsiveFullWidth = useResponsiveProp(fullWidth);

    const rootProps: Record<string, unknown> = {
      ...props,
      onOpenChange,
      onValueChange: onChange,
    };

    // add the open prop when forceOpen is true
    if (forceOpen) {
      rootProps['open'] = true;
    }

    return (
      <Root {...rootProps}>
        <StyledTrigger
          ref={forwardedRef}
          $colorScheme={colorScheme}
          $fullWidth={responsiveFullWidth}
          {...props}
        >
          <Value placeholder={placeholder} />
          <Icon
            size="md"
            type="caret-down"
          />
        </StyledTrigger>
        <Portal>
          <StyledContent
            position="popper"
            sideOffset={8}
          >
            <StyledScrollUpButton>
              <Icon
                size="sm"
                type="caret-up"
              />
            </StyledScrollUpButton>
            <Viewport>{children}</Viewport>
            <StyledScrollDownButton>
              <Icon
                size="sm"
                type="caret-down"
              />
            </StyledScrollDownButton>
          </StyledContent>
        </Portal>
      </Root>
    );
  },
);

Select.displayName = 'Select';
